import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { fileSchema } from "./files.js";
import { paisa } from "./money.js";
import { discountSchema, MANUAL_ORDER_CHANNELS, orderDetailSchema, orderLinesInputSchema, orderStatusSchema, shippingAddressInputSchema, } from "./orders.js";
/** Order edits, duplicates, confirmation and sales settings (O-03 … O-05, docs/specs/sales.md "B-32"). */
const version = z.int().min(1);
const tag = { tag: "sales" };
/**
 * `PATCH /v1/orders/{id}`: before shipping (O-05). `lines` replaces them all; totals are recomputed and a
 * confirmed or packed order's reservations follow. `deliveryChargePaisa: null` goes back to the zone's quote.
 */
export const updateOrderRequestSchema = z
    .object({
    channel: z.enum(MANUAL_ORDER_CHANNELS).optional(),
    customerId: z.uuid().nullable().optional(),
    lines: orderLinesInputSchema.optional(),
    billDiscount: discountSchema.nullable().optional(),
    shippingAddress: shippingAddressInputSchema.nullable().optional(),
    weightGrams: z.int().min(0).max(1_000_000).optional(),
    deliveryChargePaisa: paisa().min(0).nullable().optional(),
    advancePaisa: paisa().min(0).optional(),
    note: z.string().trim().max(500).optional(),
    version,
})
    .strict();
/** `POST /v1/orders/{id}/merge`: the duplicate's lines join this order; the duplicate is cancelled (O-04). */
export const mergeOrderRequestSchema = z.object({ duplicateId: z.uuid(), version }).strict();
export const MAX_BULK_ORDERS = 200;
export const bulkConfirmRequestSchema = z
    .object({ orderIds: z.array(z.uuid()).min(1).max(MAX_BULK_ORDERS) })
    .strict();
export const ORDER_BULK_JOB_STATUSES = ["queued", "running", "done"];
export const ORDER_BULK_KINDS = ["confirm", "status", "print"];
export const ORDER_DOCUMENTS = ["invoice_a4", "invoice_80mm", "packing_slip", "pick_list"];
export const orderDocumentSchema = z.enum(ORDER_DOCUMENTS);
export const orderBulkJobSchema = z.object({
    id: z.uuid(),
    kind: z.enum(ORDER_BULK_KINDS),
    status: z.enum(ORDER_BULK_JOB_STATUSES),
    /** `status` jobs: where each order goes. */
    targetStatus: orderStatusSchema.nullable(),
    /** `print` jobs: which document, and the combined PDF once done. */
    document: orderDocumentSchema.nullable(),
    file: fileSchema.nullable(),
    total: z.int(),
    results: z.array(z.object({
        orderId: z.uuid(),
        number: z.string().nullable(),
        ok: z.boolean(),
        /** The error code when it failed (`INVALID_TRANSITION`, `INSUFFICIENT_STOCK`, `NOT_FOUND`, …). */
        code: z.string().nullable(),
    })),
    createdAt: z.iso.datetime(),
});
export const confirmationSmsResponseSchema = z.object({ expiresAt: z.iso.datetime() });
/** The public confirmation page (no sign-in): only what the customer needs to recognise the order. */
export const publicOrderConfirmationSchema = z.object({
    shopName: z.string(),
    number: z.string(),
    status: orderStatusSchema,
    totalPaisa: paisa(),
    deliveryChargePaisa: paisa(),
    lines: z.array(z.object({ name: z.string(), qty: z.string(), totalPaisa: paisa() })),
    /** False once answered, expired, or the order moved on. */
    canAnswer: z.boolean(),
    customerLocale: z.enum(["en", "bn"]),
});
export const confirmationTokenParamsSchema = z.object({
    token: z.string().regex(/^[0-9a-f-]{36}\.[A-Za-z0-9_-]{43}$/),
});
export const answerConfirmationRequestSchema = z.object({ answer: z.enum(["confirm", "cancel"]) }).strict();
export const salesSettingsSchema = z.object({
    /** Another open order for the same phone within this many hours is a duplicate (O-04). */
    duplicateWindowHours: z.int(),
    /** Send the confirmation SMS by itself when a pending order is created (O-03). */
    confirmationSmsOnPending: z.boolean(),
    version: z.int(),
});
export const updateSalesSettingsRequestSchema = z
    .object({
    duplicateWindowHours: z.int().min(1).max(720).optional(),
    confirmationSmsOnPending: z.boolean().optional(),
    version: z.int().min(0),
})
    .strict();
// Endpoints ----------------------------------------------------------------------------------------------------------
export const updateOrder = defineEndpoint({
    ...tag,
    name: "updateOrder",
    method: "PATCH",
    path: "/v1/orders/{id}",
    access: { permission: "orders.manage" },
    summary: "Edit an order before it ships; reservations adjust",
    params: idParamsSchema,
    body: updateOrderRequestSchema,
    idempotent: true,
    status: 200,
    response: orderDetailSchema,
    errors: [
        "CONFLICT",
        "INVALID_TRANSITION",
        "INSUFFICIENT_STOCK",
        "VARIANT_NOT_SELLABLE",
        "INVALID_DISCOUNT",
    ],
});
export const mergeOrder = defineEndpoint({
    ...tag,
    name: "mergeOrder",
    method: "POST",
    path: "/v1/orders/{id}/merge",
    access: { permission: "orders.manage" },
    summary: "Merge a duplicate into this order (the duplicate is cancelled)",
    params: idParamsSchema,
    body: mergeOrderRequestSchema,
    idempotent: true,
    status: 200,
    response: orderDetailSchema,
    errors: ["CONFLICT", "INVALID_TRANSITION", "INSUFFICIENT_STOCK", "NOT_A_DUPLICATE"],
});
export const bulkConfirmOrders = defineEndpoint({
    ...tag,
    name: "bulkConfirmOrders",
    method: "POST",
    path: "/v1/orders/bulk-confirm",
    access: { permission: "orders.manage" },
    summary: "Confirm many orders as a job; each order's result is kept",
    body: bulkConfirmRequestSchema,
    idempotent: true,
    status: 202,
    response: orderBulkJobSchema,
});
export const getOrderBulkJob = defineEndpoint({
    ...tag,
    name: "getOrderBulkJob",
    method: "GET",
    path: "/v1/order-bulk-jobs/{id}",
    access: { permission: "orders.view" },
    summary: "A bulk job's progress and per-order results",
    params: idParamsSchema,
    status: 200,
    response: orderBulkJobSchema,
});
export const sendOrderConfirmationSms = defineEndpoint({
    ...tag,
    name: "sendOrderConfirmationSms",
    method: "POST",
    path: "/v1/orders/{id}/confirmation-sms",
    access: { permission: "orders.manage" },
    summary: "Text the customer a link to confirm or cancel (48 h, single use)",
    params: idParamsSchema,
    status: 200,
    response: confirmationSmsResponseSchema,
    errors: ["INVALID_TRANSITION", "NO_PHONE"],
});
export const getPublicOrderConfirmation = defineEndpoint({
    ...tag,
    name: "getPublicOrderConfirmation",
    method: "GET",
    path: "/v1/public/order-confirmations/{token}",
    access: { public: true },
    summary: "What the customer sees behind the confirmation link",
    params: confirmationTokenParamsSchema,
    status: 200,
    response: publicOrderConfirmationSchema,
    errors: ["RATE_LIMITED"],
});
export const answerPublicOrderConfirmation = defineEndpoint({
    ...tag,
    name: "answerPublicOrderConfirmation",
    method: "POST",
    path: "/v1/public/order-confirmations/{token}",
    access: { public: true },
    summary: "The customer confirms or cancels",
    params: confirmationTokenParamsSchema,
    body: answerConfirmationRequestSchema,
    status: 200,
    response: publicOrderConfirmationSchema,
    errors: ["RATE_LIMITED", "INVALID_TRANSITION"],
});
export const getSalesSettings = defineEndpoint({
    ...tag,
    name: "getSalesSettings",
    method: "GET",
    path: "/v1/sales-settings",
    access: { permission: "orders.view" },
    summary: "Duplicate window and confirmation SMS settings",
    status: 200,
    response: salesSettingsSchema,
});
export const updateSalesSettings = defineEndpoint({
    ...tag,
    name: "updateSalesSettings",
    method: "PATCH",
    path: "/v1/sales-settings",
    access: { permission: "settings.manage" },
    summary: "Change the duplicate window or confirmation SMS settings",
    body: updateSalesSettingsRequestSchema,
    status: 200,
    response: salesSettingsSchema,
    errors: ["CONFLICT"],
});
//# sourceMappingURL=order-workflow.js.map